import { Building2, Star } from "lucide-react";
import { useBrandsStore } from "@/stores/brands";
import { useWorkspacesStore } from "@/stores/workspaces";
import { cn } from "@/lib/utils";

interface BrandSelectorProps {
  selected: Set<number>;
  onChange: (selected: Set<number>) => void;
}

export function BrandSelector({ selected, onChange }: BrandSelectorProps) {
  const { brands, loading } = useBrandsStore();
  const primaryBrandId = useWorkspacesStore(s => s.current?.primary_brand_id);

  if (loading) {
    return (
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <div
            key={i}
            className="h-10 rounded-md bg-slate-100 animate-subtle-pulse"
          />
        ))}
      </div>
    );
  }

  if (brands.length === 0) {
    return (
      <div className="text-center py-6">
        <Building2 className="w-5 h-5 text-slate-300 mx-auto mb-2" />
        <p className="text-[13px] text-slate-500">
          No brands yet. Add brands in the Library to get started.
        </p>
      </div>
    );
  }

  const toggle = (id: number) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange(next);
  };

  // Sort primary brand first
  const sortedBrands = [...brands].sort((a, b) => {
    if (a.id === primaryBrandId) return -1;
    if (b.id === primaryBrandId) return 1;
    return 0;
  });

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
      {sortedBrands.map((brand) => {
        const isSelected = selected.has(brand.id);
        const isPrimary = brand.id === primaryBrandId;
        return (
          <button
            key={brand.id}
            type="button"
            onClick={() => toggle(brand.id)}
            className={cn(
              "flex items-center gap-2.5 px-3 py-2.5 rounded-md border text-left text-[13px] font-medium transition-all duration-150 cursor-pointer",
              isSelected
                ? "border-brand-600 bg-brand-50 text-brand-950 ring-1 ring-brand-600/20"
                : "border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50"
            )}
          >
            {isPrimary ? (
              <Star
                className={cn(
                  "w-3.5 h-3.5 shrink-0 fill-accent-500",
                  "text-accent-500"
                )}
              />
            ) : (
              <div
                className={cn(
                  "w-2 h-2 rounded-full shrink-0 transition-colors",
                  isSelected ? "bg-brand-600" : "bg-slate-200"
                )}
              />
            )}
            <span className="truncate">{brand.name}</span>
          </button>
        );
      })}
    </div>
  );
}
